import * as views from '@quizzach/views';

export enum RoutePath {
	Homepage = '/',
	NotFound = '/not-found',
	ActivateUser = '/user/activate/:id/:code',
	ResetPassword = '/user/reset-password',
	SetNewPassword = '/user/reset-password/:id/:code',
	Login = '/login',
	Register = '/register',
	RegistrationSuccess = '/register/success',

	Profile = '/profile',
	ProfileQuizzes = 'quizzes',
	ProfileTransactions = 'transactions',
	ProfileSettings = 'settings',
	ProfileAuthorAnalytics = 'author-analytics',

	ProfileAvatar = '/profile/avatar',
	Voucher = '/gift',
	VoucherRedeem = '/gift/redeem',
	GeneratorCustomizer = '/generator/customizer',
	GeneratorSummary = '/generator/summary',
	GeneratorEnd = '/generator/end',
	QuickPick = '/quick/pick',
	QuickSummary = '/quick/summary',
	QuickEnd = '/quick/end',

	Quiz = '/quiz/:uuid',
	QuizPlay = 'play',
	QuizModeSelection = 'mode',
	QuizStart = 'start',
	QuizPuzzleIntro = 'puzzle-intro',
	QuizPuzzleAnswer = 'puzzle-answer',
	QuizQuestionSlides = 'question/:number',
	QuizEnd = 'end',
	QuizMidBreak1 = 'first-half-mid-break',
	QuizMidBreak2 = 'second-half-mid-break',
	QuizHalftime = 'halftime',
	QuizMidBreakSingle = 'break',
	QuizEstimationIntro = 'estimation-intro',
	QuizAnswerSlides = 'answer/:number',
	QuizChapter = 'chapter/:chapterNumber',
	QuizQuestionChapter = 'question-chapter/:chapterNumber',
	QuizQuestionChapterEnd = 'question-chapter-end/:chapterNumber',
	QuizAnswerChapter = 'answer-chapter/:chapterNumber',
	QuizAnswerChapterEnd = 'answer-chapter-end/:chapterNumber',

	PremadeQuizzes = '/premade',
	PremadeQuizzesById = 'quiz/:uuid',
	PremadeQuizzesByTag = 'tag/:tag',
	PremadeQuizzesAuthor = 'author',
	PremadeQuizzesByAuthor = 'author/:author',
	PremadeSearch = 'search',

	QuizQuestionPreview = '/preview/question/:id',
	QuizAnswerPreview = '/preview/answer/:id',
	Summary = '/summary',
	OrderFinish = '/order-finish',
	OrderFinishProvider = '/order/:uuid',
	PrivacyPolicy = '/privacy-policy',
	CookiePolicy = '/cookie-policy',
	PaymentProviderSecurity = '/provider-policy',
	AboutUs = '/about-us',
	Faq = '/faq',
	TermsOfUse = '/terms-of-use',
	Info = '/info',
	LandingPromoRetrospective = '/promo-retrospective',
}

type AuthorizationLevel = 'public' | 'authenticated';

export interface AccessControlListProps {
	element: React.ReactNode;
	path: string;
	authorizationLevel: AuthorizationLevel;
}

export interface BaseRoute<T = object> {
	key: string;
	path: string;
	exact: boolean;
	authorizationLevel: AuthorizationLevel;
	component: React.FC<T>;
	children?: any;
}

export const AppRoutes: BaseRoute[] = [
	{
		key: 'homepage',
		path: RoutePath.Homepage,
		exact: true,
		authorizationLevel: 'public',
		component: views.Homepage,
	},
	{
		key: 'not-found',
		path: RoutePath.NotFound,
		exact: true,
		authorizationLevel: 'public',
		component: views.NotFound,
	},
	{
		key: 'activate-user',
		path: RoutePath.ActivateUser,
		exact: true,
		authorizationLevel: 'public',
		component: views.ActivateAccount,
	},
	{
		key: 'reset-password',
		path: RoutePath.ResetPassword,
		exact: true,
		authorizationLevel: 'public',
		component: views.ResetPasswordStep1,
	},
	{
		key: 'set-new-password',
		path: RoutePath.SetNewPassword,
		exact: true,
		authorizationLevel: 'public',
		component: views.ResetPasswordStep2,
	},
	{
		key: 'login',
		path: RoutePath.Login,
		exact: true,
		authorizationLevel: 'public',
		component: views.Auth,
	},
	{
		key: 'register',
		path: RoutePath.Register,
		exact: true,
		authorizationLevel: 'public',
		component: views.Auth,
	},
	{
		key: 'register',
		path: RoutePath.RegistrationSuccess,
		exact: true,
		authorizationLevel: 'public',
		component: views.RegistrationSuccess,
	},
	{
		key: 'profile',
		path: RoutePath.Profile,
		exact: false,
		authorizationLevel: 'authenticated',
		component: views.Profile,
		children: [
			{
				key: 'profile-quizzes',
				path: RoutePath.ProfileQuizzes,
				exact: true,
				authorizationLevel: 'authenticated',
				component: views.UserQuizzes,
			},
			{
				key: 'profile-transactions',
				path: RoutePath.ProfileTransactions,
				exact: true,
				authorizationLevel: 'authenticated',
				component: views.UserStatsHistory,
			},
			{
				key: 'profile-settings',
				path: RoutePath.ProfileSettings,
				exact: true,
				authorizationLevel: 'authenticated',
				component: views.UserSettings,
			},
			{
				key: 'profile-author',
				path: RoutePath.ProfileAuthorAnalytics,
				exact: true,
				authorizationLevel: 'authenticated',
				component: views.UserAuthorAnalytics,
			},
		],
	},

	{
		key: 'voucher',
		path: RoutePath.Voucher,
		exact: true,
		authorizationLevel: 'public',
		component: views.VoucherShop,
	},
	{
		key: 'voucher-redeem',
		path: RoutePath.VoucherRedeem,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.UserVoucher,
	},

	{
		key: 'avatar',
		path: RoutePath.ProfileAvatar,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.ProfileAvatar,
	},
	{
		key: 'generator-customizer',
		path: RoutePath.GeneratorCustomizer,
		exact: true,
		authorizationLevel: 'public',
		component: views.GeneratorQuizCustomizer,
	},
	{
		key: 'generator-summary',
		path: RoutePath.GeneratorSummary,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.CartSummary,
	},
	{
		key: 'generator-end',
		path: RoutePath.GeneratorEnd,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.OrderFinish,
	},
	{
		key: 'quick-pick',
		path: RoutePath.QuickPick,
		exact: true,
		authorizationLevel: 'public',
		component: views.QuickPick,
	},
	{
		key: 'quick-summary',
		path: RoutePath.QuickSummary,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.CartSummary,
	},
	{
		key: 'quick-end',
		path: RoutePath.QuickEnd,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.OrderFinish,
	},

	{
		key: 'quiz',
		path: RoutePath.Quiz,
		exact: false,
		authorizationLevel: 'authenticated',
		component: views.Quiz,
		children: [
			{
				key: 'quiz',
				path: RoutePath.QuizPlay,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizPlay,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizModeSelection,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizModeSelection,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizStart,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizIntro,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizPuzzleIntro,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizPuzzleIntro,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizPuzzleAnswer,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizPuzzleAnswer,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizQuestionSlides,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuestionSlides,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizEnd,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizBreak,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizMidBreak1,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizBreak,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizMidBreak2,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizBreak,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizHalftime,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizBreak,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizMidBreakSingle,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizBreak,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizEstimationIntro,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizBreak,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizChapter,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizChapter,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizQuestionChapter,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizChapter,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizQuestionChapterEnd,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizChapter,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizAnswerChapter,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizChapter,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizAnswerChapterEnd,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.QuizChapter,
			},
			{
				key: 'quiz',
				path: RoutePath.QuizAnswerSlides,
				exact: false,
				authorizationLevel: 'authenticated',
				component: views.AnswerSlides,
			},
		],
	},
	{
		key: 'premade-quizzes',
		path: RoutePath.PremadeQuizzes,
		exact: false,
		authorizationLevel: 'public',
		component: views.PremadeRoot,
		children: [
			{
				key: 'premade-quizzes-by-tag',
				path: RoutePath.PremadeQuizzesByTag,
				exact: false,
				authorizationLevel: 'public',
				component: views.PremadeQuizzesByTag,
			},
			{
				key: 'premade-quizzes-by-author',
				path: RoutePath.PremadeQuizzesByAuthor,
				exact: false,
				authorizationLevel: 'public',
				component: views.PremadeQuizzesByAuthor,
			},
			{
				key: 'premade-quizzes-by-id',
				path: RoutePath.PremadeQuizzesById,
				exact: false,
				authorizationLevel: 'public',
				component: views.PremadeQuizzesById,
			},
			{
				key: 'premade-search',
				path: RoutePath.PremadeSearch,
				exact: true,
				authorizationLevel: 'public',
				component: views.PremadeSearch,
			},
			{
				key: 'premade-author',
				path: RoutePath.PremadeQuizzesAuthor,
				exact: true,
				authorizationLevel: 'public',
				component: views.PremadeQuizzesAuthor,
			},
		],
	},

	{
		key: 'question-preview',
		path: RoutePath.QuizQuestionPreview,
		exact: true,
		authorizationLevel: 'public',
		component: views.AdminQuizQuestionPreview,
	},
	{
		key: 'answer-preview',
		path: RoutePath.QuizAnswerPreview,
		exact: true,
		authorizationLevel: 'public',
		component: views.AdminQuizAnswerPreview,
	},
	{
		key: 'cart-summary',
		path: RoutePath.Summary,
		exact: true,
		authorizationLevel: 'public',
		component: views.CartSummary,
	},
	{
		key: 'order-finish',
		path: RoutePath.OrderFinish,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.OrderFinish,
	},
	{
		key: 'order-finish-payment',
		path: RoutePath.OrderFinishProvider,
		exact: true,
		authorizationLevel: 'authenticated',
		component: views.OrderFinishPayment,
	},
	{
		key: 'cookie-policy',
		path: RoutePath.CookiePolicy,
		exact: true,
		authorizationLevel: 'public',
		component: views.CookiePolicy,
	},
	{
		key: 'privacy-policy',
		path: RoutePath.PrivacyPolicy,
		exact: true,
		authorizationLevel: 'public',
		component: views.PrivacyPolicy,
	},
	{
		key: 'terms-of-use',
		path: RoutePath.TermsOfUse,
		exact: true,
		authorizationLevel: 'public',
		component: views.TermsOfUse,
	},
	{
		key: 'about-us',
		path: RoutePath.AboutUs,
		exact: true,
		authorizationLevel: 'public',
		component: views.AboutUs,
	},
	{
		key: 'payment-security',
		path: RoutePath.PaymentProviderSecurity,
		exact: true,
		authorizationLevel: 'public',
		component: views.PaymentProviderSecurity,
	},
	{
		key: 'faq',
		path: RoutePath.Faq,
		exact: true,
		authorizationLevel: 'public',
		component: views.Instructions,
	},
	{
		key: 'info',
		path: RoutePath.Info,
		exact: true,
		authorizationLevel: 'public',
		component: views.Info,
	},
	{
		key: 'promo-retrospective',
		path: RoutePath.LandingPromoRetrospective,
		exact: true,
		authorizationLevel: 'public',
		component: views.PromoRetrospective,
	},
];
